import React from 'react';
import { IStorefrontBundleView, IStorefrontProductView } from '../../../../models/StorefrontProducts';
import { Icon, IconButton, IButtonStyles, IRawStyle } from '@fluentui/react';
import { PLACEHOLDER_IMAGE } from '../../../../Constants';
import { EditPencil, IProductCardStyles } from '../styleControls';
import { useCardChrome } from '../StorefrontCardChromeContext';

// Inline styles resolved from the owning section's `productStyleProps` (including a card-type
// `$styleRef` to a named Storefront Style) — see productCardStylesFrom. Absent = the card renders
// exactly as it always has, from the stylesheet alone.
interface IProductCardProps {
    product: IStorefrontProductView;
    isDark?: boolean;
    // When provided (editor edit mode), the card becomes the affordance for its own styling: the
    // hover pencil is only the visible hint — a click anywhere on the card opens the quick style
    // popover. The card ROOT element is handed back so the caller can anchor a Callout on the exact
    // card that was clicked (a section renders many identical cards; the handler alone can't tell
    // them apart).
    onEditStyle?: (target: HTMLElement) => void;
    cardStyles?: IProductCardStyles;
}

interface IBundleCardProps {
    bundle: IStorefrontBundleView;
    isDark?: boolean;
    onEditStyle?: (target: HTMLElement) => void;
    cardStyles?: IProductCardStyles;
}

// Matches the real shopper storefront product-card add-to-cart button: a clean blue
// circular icon button with a white cart glyph. Applied via the Fluent v8 `styles`
// prop (not just the CSS class) because Fluent injects each component's default styles
// into <head> at runtime with equal (single-class) specificity but later source order,
// so a static stylesheet class loses the cascade and the theme's default IconButton
// look (transparent/olive hover + focus border) bleeds through. The `styles` prop is
// merged by Fluent with precedence, so it reliably overrides those defaults.
// `addToCart` is the merchant's own button styling (homePageConfig.addToCartButton, delivered by
// StorefrontCardChromeProvider). It is spread LAST over every state so a configured background —
// the orange one on a Star-Trek-themed store, say — wins over the blue default in the resting,
// hovered, pressed and focused states alike. Without that, the hover/pressed entries below would
// snap the button back to blue the moment the pointer touched it, which is exactly the kind of
// "looks right until you interact with it" mismatch this preview exists to prevent.
// The cast is the usual Fluent v8 seam: IStyle and React.CSSProperties disagree on a handful of
// property unions, so a plain CSSProperties spread won't satisfy IStyle even though every value
// here is a legal CSS declaration at runtime.
const cartButtonStylesWith = (style?: React.CSSProperties): IButtonStyles => {
    const addToCart = style as IRawStyle | undefined;
    return {
    root: {
        position: 'absolute',
        right: 12,
        bottom: 12,
        zIndex: 2,
        width: 32,
        height: 30,
        minWidth: 0,
        padding: 0,
        border: 'none',
        borderRadius: '50%',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
        backgroundColor: '#0f6cbd',
        color: '#ffffff',
        ...addToCart,
    },
    rootHovered: { backgroundColor: '#0c3b5e', color: '#ffffff', ...addToCart },
    rootPressed: { backgroundColor: '#0c3b5e', color: '#ffffff', ...addToCart },
    rootFocused: { backgroundColor: '#0f6cbd', color: '#ffffff', ...addToCart },
    // The glyph follows the configured text color when one is set, so a light button gets a dark
    // cart rather than an invisible white one.
    icon: { color: addToCart?.color ?? '#ffffff', fontSize: 16 },
    iconHovered: { color: addToCart?.color ?? '#ffffff' },
    iconPressed: { color: addToCart?.color ?? '#ffffff' },
    };
};

// The runtime's FavoriteButton, reduced to its appearance. Same class names as the storefront
// (.sf-favorite-btn + the .sf-favorite-overlay placement) so the ported CSS positions it
// identically; no toggle state, because a preview has no shopper whose favorites it could reflect.
//
// Always the outline heart: a filled one would claim this specific product is favorited, which is
// a per-shopper fact the merchant is not editing here.
const PreviewFavoriteHeart: React.FC<{ onClick: (e: React.MouseEvent<unknown>) => void }> = ({ onClick }) => (
    <button
        type='button'
        // Inert decoration, but still a real button so it sits in the same box the storefront's does.
        tabIndex={-1}
        aria-hidden='true'
        className='sf-favorite-btn sf-favorite-overlay'
        onClick={onClick}
    >
        <Icon iconName='Heart' />
    </button>
);

// Click wiring shared by both card flavors. The ref exists purely to hand the popover an anchor:
// the callback fires with the card's own root <div>, so the Callout lands on the clicked card
// rather than on some ancestor common to the whole row.
const useCardStyleOpener = (onEditStyle?: (target: HTMLElement) => void) => {
    const rootRef = React.useRef<HTMLDivElement>(null);
    const open = React.useCallback(() => {
        if (rootRef.current) onEditStyle?.(rootRef.current);
    }, [onEditStyle]);
    return { rootRef, open, editable: !!onEditStyle };
};

// The cart button is decoration in the preview, but it sits INSIDE the now-clickable card, so its
// clicks have to be swallowed — otherwise "add to cart" opens the style popover.
const stopCardClick = (e: React.MouseEvent<unknown>) => e.stopPropagation();

const truncateText = (text: string, maxLength: number, truncateOnWord = true, includeEllipsis = true): string => {
    if (text.length <= maxLength) {
        return text;
    }

    const truncated = truncateOnWord ? text.substring(0, text.lastIndexOf(' ', maxLength)) : text.substring(0, maxLength);

    return `${truncated}${includeEllipsis ? '...' : ''}`;
};

export const ProductCard: React.FC<IProductCardProps> = (props) => {
    const { rootRef, open, editable } = useCardStyleOpener(props.onEditStyle);
    const chrome = useCardChrome();
    return <div
        ref={rootRef}
        className={`storefront-product-card${props.isDark ? ' storefront-product-card-dark' : ''}${editable ? ' sf-pencil-host' : ''}`}
        // cursor first so a card style that sets its own cursor still wins.
        style={{ ...(editable ? { cursor: 'pointer' } : {}), ...props.cardStyles?.card }}
        onClick={editable ? open : undefined}
    >
        {editable && <EditPencil onClick={open} title='Edit card style' />}
        <div className='storefront-product-card-image' style={props.cardStyles?.image}>
            <img src={props.product.image && props.product.image !== '' ? props.product.image : PLACEHOLDER_IMAGE} alt={props.product.name ?? 'Product Image'} style={{ width: '100%' }} />
        </div>
        <div className='storefront-product-card-name' style={props.cardStyles?.title}>
            {truncateText(props.product.name ?? '', 50)}
        </div>
        <div className='storefront-product-card-price' style={props.cardStyles?.price}>
            {props.product.basePrice ? `$${props.product.basePrice?.toFixed(2)}` : ''}
        </div>
        {chrome.showFavorites && <PreviewFavoriteHeart onClick={stopCardClick} />}
        <IconButton className='storefront-product-card-cart-button' styles={cartButtonStylesWith(chrome.addToCartStyle)} iconProps={{ iconName: 'ShoppingCart' }} title='Add to Cart' ariaLabel='Add to Cart' onClick={stopCardClick} />
    </div>
}

export const BundleCard: React.FC<IBundleCardProps> = (props) => {
    // Bundle sections style their tiles from the same section `productStyleProps` dict, so the
    // affordance is identical — one popover serves both card flavors.
    const { rootRef, open, editable } = useCardStyleOpener(props.onEditStyle);
    const chrome = useCardChrome();
    return <div
        ref={rootRef}
        className={`storefront-product-card${props.isDark ? ' storefront-product-card-dark' : ''}${editable ? ' sf-pencil-host' : ''}`}
        style={{ ...(editable ? { cursor: 'pointer' } : {}), ...props.cardStyles?.card }}
        onClick={editable ? open : undefined}
    >
        {editable && <EditPencil onClick={open} title='Edit card style' />}
        <div className='storefront-product-card-image' style={props.cardStyles?.image}>
            <img src={props.bundle.image && props.bundle.image !== '' ? props.bundle.image : PLACEHOLDER_IMAGE} alt={props.bundle.name ?? 'Bundle Image'} style={{ width: '100%' }} />
        </div>
        <div className='storefront-product-card-name' style={props.cardStyles?.title}>
            {truncateText(props.bundle.name ?? '', 50)}
        </div>
        <div className='storefront-product-card-price' style={props.cardStyles?.price}>
            {props.bundle.price ? `$${props.bundle.price?.toFixed(2)}` : ''}
        </div>
        {chrome.showFavorites && <PreviewFavoriteHeart onClick={stopCardClick} />}
        <IconButton className='storefront-product-card-cart-button' styles={cartButtonStylesWith(chrome.addToCartStyle)} iconProps={{ iconName: 'ShoppingCart' }} title='Add to Cart' ariaLabel='Add to Cart' onClick={stopCardClick} />
    </div>
}